import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Eye,
  CalendarCheck2,
  TrendingUp,
  Scale,
  Handshake,
  Building,
  Compass,
  Ruler,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import YonetimKuruluCard from "@/components/YonetimKuruluCard";
import { useDocumentSEO } from "@/hooks/useDocumentSEO";

const YAVUZ_PHOTO =
  "https://customer-assets.emergentagent.com/job_unruffled-noether-8/artifacts/a5nhdfgm_image.png";
const CIHAN_PHOTO =
  "https://customer-assets.emergentagent.com/job_unruffled-noether-8/artifacts/snuniivc_image.png";

const YONETIM_KURULU = [
  {
    slug: "yavuz-fettahoglu",
    name: "Yavuz Fettahoğlu",
    title: "Yönetim Kurulu Başkanı",
    photo: YAVUZ_PHOTO,
    bio:
      "Yavuz Fettahoğlu, Sakarya Üniversitesi Mühendislik Fakültesi Jeofizik Mühendisliği bölümünden mezun olmuş, işletme alanında yüksek lisans (MBA) eğitimini tamamlamıştır. İnşaat, iş makineleri ve teknoloji sektörlerinde uzun yıllara dayanan girişimci ve yönetici tecrübesine sahip olan Fettahoğlu, halihazırda makine ihracatında öne çıkan firmalar arasında yer alan MOPU şirketinin Üst Yöneticisi (CEO) olarak görev yapmaktadır. İş dünyasındaki faaliyetlerinin yanı sıra, Genç MÜSİAD 7. Dönem Genel Başkanlığı görevini üstlenerek sivil toplum alanında da uzun soluklu bir liderlik tecrübesi edinmiştir. Bu kurumsal ve ticari birikimle 1001 Mimarlık İnşaat ve Ticaret A.Ş. Yönetim Kurulu Başkanlığı görevini yürütmektedir.",
  },
  {
    slug: "cihan-benli",
    name: "Cihan Benli",
    title: "Yönetim Kurulu Başkan Vekili",
    photo: CIHAN_PHOTO,
    bio:
      "Cihan Benli, iş hayatına 2003 yılında başlamış, edindiği tecrübe ve girişimcilik vizyonuyla 2009 yılında iş ortağı Metehan Gül ile birlikte 1001 Grup'un temellerini atmıştır. Reklam, baskı, tanıtım ve organizasyon alanlarında faaliyet gösteren 1001 Grup'un kurucu ortağı ve yöneticisi olarak çalışmalarını sürdüren Benli, iş dünyasının yanı sıra sivil toplum alanında da aktif görevler üstlenmektedir. MÜSİAD çatısı altında Genç MÜSİAD'ın çeşitli kademelerinde görev almasının ardından, Haziran 2026'da Sultanbeyli Girişimci İş Adamları Derneği (SUGİAD) 9. Dönem Yönetim Kurulu Başkanlığına seçilen Benli, öncesinde SUGİAD bünyesinde yönetim kurulu üyeliği ve başkan yardımcılığı görevlerinde de bulunmuştur. Bu kurumsal ve ticari birikimle 1001 Mimarlık İnşaat ve Ticaret A.Ş. Yönetim Kurulu Başkan Vekilliği görevini yürütmektedir.",
  },
];

const YONETIM_JSONLD = YONETIM_KURULU.map((m) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: m.name,
  jobTitle: m.title,
  worksFor: {
    "@type": "Organization",
    name: "1001 Mimarlık İnşaat ve Ticaret A.Ş.",
  },
  description: m.bio,
  image: m.photo,
}));

const GRUP_IMAGE =
  "https://images.pexels.com/photos/5323853/pexels-photo-5323853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const DEGERLER = [
  {
    icon: Eye,
    title: "Şeffaflık",
    body: "Karar, süreç ve maliyetlerde izlenebilir ve açık iletişim anlayışı.",
  },
  {
    icon: CalendarCheck2,
    title: "Planlılık ve Disiplin",
    body: "Kapsam, bütçe ve zaman planına sadakatle yürütülen proje disiplini.",
  },
  {
    icon: TrendingUp,
    title: "Uzun Vadeli Değer Üretimi",
    body: "Kısa vadeli çıktı yerine kalıcı ve sürdürülebilir değere odaklanma.",
  },
  {
    icon: Scale,
    title: "Mimari ve Ticari Bakışın Dengesi",
    body: "Tasarım hassasiyeti ile yatırım perspektifini bir arada gözetme.",
  },
  {
    icon: Handshake,
    title: "Güvenilir İş Ortaklığı",
    body: "Karşılıklı sorumluluk ve sürdürülebilir iş ilişkisi anlayışı.",
  },
];

export default function KurumsalPage() {
  useDocumentSEO({
    title: "Kurumsal",
    description:
      "1001 Grup'un yapı sektöründeki iştiraki olarak kurumsal disiplin ve mimari bakış açısıyla faaliyet gösteren 1001 Mimarlık İnşaat A.Ş. hakkında bilgi.",
  });
  return (
    <>
      <PageHeader
        breadcrumb="Kurumsal"
        overline="Hakkımızda"
        title="Kurumsal"
        subtitle="Kurumsal disiplinle kurulan, mimari bakış açısını ticari perspektifle bir araya getiren bir yapı."
        backgroundImage="https://images.pexels.com/photos/15577446/pexels-photo-15577446.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800"
      />

      {/* 1. ANA METİN */}
      <section
        data-testid="kurumsal-ana-metin"
        className="bg-white py-24 lg:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <span className="w-10 h-[1px] bg-gold" />
              <span className="overline text-anthracite/60">Şirket Profili</span>
            </div>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
              Kalıcı, güvenilir ve
              <br />
              <span className="text-gold italic font-extralight">değer üreten projeler.</span>
            </h2>
            <p className="mt-8 max-w-3xl text-base md:text-lg text-anthracite/85 leading-relaxed font-light">
              1001 Mimarlık İnşaat ve Ticaret A.Ş.,{" "}
              <span className="text-navy font-medium">
                1001 Grup &amp; Yenilik Makina İnşaat&apos;ın
              </span>{" "}
              kurumsal iş yapma kültürü, ticari disiplini ve proje yönetimi
              tecrübesiyle yapı sektöründe faaliyet göstermek üzere
              kurulmuştur. Mimari bakış açısını, uygulama kabiliyeti ve yatırım
              perspektifiyle bir araya getirerek kalıcı, güvenilir ve değer
              üreten projeler geliştirmeyi hedefler.
            </p>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-[1px] bg-navy/10">
              {[
                { icon: Compass, label: "Mimari Bakış" },
                { icon: Building, label: "Uygulama Kabiliyeti" },
                { icon: Ruler, label: "Yatırım Perspektifi" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="bg-white p-6 flex items-center gap-4 min-h-[96px]"
                >
                  <span className="w-10 h-10 border border-navy/15 flex items-center justify-center text-gold shrink-0">
                    <Icon size={16} strokeWidth={1.5} />
                  </span>
                  <div className="font-heading text-[14px] tracking-tight text-navy">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. 1001 GRUP BAĞLANTISI */}
      <section
        data-testid="kurumsal-grup-baglantisi"
        className="bg-anthracite text-white py-24 lg:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5 relative">
            <div className="relative overflow-hidden">
              <img
                src={GRUP_IMAGE}
                alt="Kurumsal mimari"
                className="w-full h-full object-cover aspect-[4/5] md:aspect-auto md:h-[520px]"
              />
              <div className="absolute inset-0 bg-navy/40 mix-blend-multiply" />
              <div className="absolute inset-0 border border-gold/30" />
              {/* corner details */}
              <div className="absolute top-4 left-4 w-6 h-[1px] bg-gold" />
              <div className="absolute top-4 left-4 w-[1px] h-6 bg-gold" />
              <div className="absolute bottom-4 right-4 w-6 h-[1px] bg-gold" />
              <div className="absolute bottom-4 right-4 w-[1px] h-6 bg-gold" />
            </div>
            <div className="mt-6 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/50">
              <span className="w-8 h-[1px] bg-gold" />
              Kurumsal Bağ
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-[1px] bg-gold" />
              <span className="overline text-gold">Grup Bağlantısı</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              1001 Grup Bağlantısı
            </h2>
            <div className="mt-6 w-16 h-[1px] bg-gold/60" />

            <div className="mt-8 space-y-6 text-base md:text-[17px] leading-relaxed text-white/80 font-light">
              <p>
                <span className="text-white font-medium">
                  1001 Grup &amp; Yenilik Makina İnşaat
                </span>
                , farklı sektörlerde faaliyet gösteren, kurumsal iş yapma
                kültürünü ve ticari birikimi öne çıkaran çok sektörlü bir yapıdır.
                Uzun soluklu ticaret geleneği, disiplinli yönetim anlayışı ve
                süreç odaklı iş yapma biçimi bu yapının kimliğinin temelini
                oluşturur.
              </p>
              <p>
                1001 Mimarlık İnşaat ve Ticaret A.Ş., bu birikimin
                <span className="text-gold">
                  {" "}
                  yapı sektöründeki iştiraki
                </span>{" "}
                olarak; mevcut kurumsal disiplini, ticari yetkinliği ve yönetim
                anlayışını mimarlık ve inşaat alanına taşımak üzere kurulmuştur.
              </p>
              <p>
                Şirket faaliyetlerini bu kurumsal referans üzerinden yürütür;
                kararlarını uzun soluklu iş yapma anlayışıyla uyumlu biçimde
                alır.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. NEDEN YENİ BİR YAPI ŞİRKETİ? */}
      <section
        data-testid="kurumsal-neden-yeni"
        className="bg-white py-24 lg:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <span className="w-10 h-[1px] bg-gold" />
              <span className="overline text-anthracite/60">Konumlanma</span>
            </div>
            <h2 className="mt-8 font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
              Neden yeni bir
              <br />
              <span className="text-gold italic font-extralight">yapı şirketi?</span>
            </h2>
          </div>

          <div className="md:col-span-8 md:col-start-5">
            <div className="space-y-6 text-base md:text-[17px] leading-relaxed text-anthracite/85 font-light">
              <p>
                Şirketimizin inşaat alanındaki yürütme ortağı,{" "}
                <span className="text-navy font-medium">
                  sektörde uzun yıllara dayanan saha tecrübesine
                </span>{" "}
                sahiptir. Bugüne kadar farklı ölçek ve nitelikteki projelere alt yüklenici olarak
                katkı sağlamış; bu süreçte planlama, uygulama ve teslim disiplinini pekiştirmiştir.
              </p>
              <p>
                Bu birikimi doğrudan mimari projelendirme, inşaat taahhüt ve anahtar teslim
                uygulamalara taşımak; kararların sahaya olduğu kadar tasarım ve yönetim masasına
                da eşit hâkim biçimde alınabileceği bir yapı kurmak amacıyla 1001 Mimarlık İnşaat
                ve Ticaret A.Ş. kurulmuştur.
              </p>

              <div className="pt-2">
                <blockquote className="relative pl-6 border-l border-gold py-2">
                  <span className="absolute -top-1 -left-[6px] w-3 h-3 bg-gold" />
                  <p className="font-heading text-xl md:text-2xl font-light text-navy leading-snug italic">
                    Sıfırdan başlayan bir marka değil; yılların saha tecrübesini kurumsal bir çatı
                    altında, kendi adına projeler geliştirmek üzere yeniden konumlandıran bir yapı.
                  </p>
                </blockquote>
              </div>

              <p className="pt-2">
                Amacımız, kurumsal iş yapma anlayışıyla saha kabiliyetini aynı çatı altında
                buluşturarak; müşterilerimize sade, planlı ve mimari değeri gözeten bir yapı
                deneyimi sunmaktır.
              </p>
            </div>

            {/* Highlight bar */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-[1px] bg-navy/10">
              {[
                { k: "Saha", v: "Uzun yıllara dayanan uygulama tecrübesi" },
                { k: "Kurumsal", v: "1001 Grup&apos;un iş yapma disiplini" },
                { k: "Vizyon", v: "Kendi adına projeler geliştiren yapı" },
              ].map((item) => (
                <div
                  key={item.k}
                  className="bg-white p-6 min-h-[130px] flex flex-col justify-between border border-navy/5"
                >
                  <div className="overline text-anthracite/60">{item.k}</div>
                  <div
                    className="mt-4 text-[13.5px] leading-relaxed text-navy font-light"
                    dangerouslySetInnerHTML={{ __html: item.v }}
                  />
                  <div className="mt-4 w-6 h-[1px] bg-gold" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. KURUMSAL DEĞERLERİMİZ */}
      <section
        data-testid="kurumsal-degerler"
        className="bg-navy text-white py-24 lg:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-gold">Değerlerimiz</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
                Kurumsal
                <br />
                Değerlerimiz
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base leading-relaxed text-white/75 font-light max-w-lg">
                İş yapma biçimimizi tanımlayan değerler; sahayı, tasarım masasını ve yönetim
                salonunu aynı disiplinle bağlar.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/10">
            {DEGERLER.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                data-testid={`kurumsal-deger-${i}`}
                className="group bg-navy hover:bg-anthracite transition-colors p-8 lg:p-10 flex flex-col min-h-[240px]"
              >
                <div className="flex items-center">
                  <span className="w-11 h-11 border border-gold/50 flex items-center justify-center text-gold shrink-0 group-hover:bg-gold group-hover:text-white transition-colors">
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                </div>
                <h3 className="mt-6 font-heading text-lg font-medium">{title}</h3>
                <p className="mt-3 text-[13.5px] leading-relaxed text-white/70 font-light">
                  {body}
                </p>
                <div className="mt-6 w-8 h-[1px] bg-gold group-hover:w-12 transition-all" />
              </div>
            ))}
            {/* Filler cell to keep 6-cell grid symmetric on lg */}
            <div className="hidden lg:flex bg-navy p-8 lg:p-10 flex-col justify-between min-h-[240px] border-l border-white/5">
              <div className="overline text-gold/80">1001 Mimarlık İnşaat</div>
              <div>
                <p className="font-heading text-lg font-light text-white/85 leading-snug">
                  Sade, disiplinli ve mimari değeri gözeten bir yapı yaklaşımı.
                </p>
                <div className="mt-6 w-16 h-[1px] bg-gold" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. YÖNETİM KURULU */}
      <section
        data-testid="kurumsal-yonetim"
        className="bg-white py-24 lg:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />

        {/* JSON-LD structured data — schema.org Person */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(YONETIM_JSONLD) }}
        />

        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-anthracite/70">Yönetim</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
                Yönetim Kurulu
              </h2>
              <div className="mt-6 w-16 h-[1px] bg-gold" />
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-base leading-relaxed text-anthracite/80 font-light">
                Şirketimizin yönetim anlayışını şekillendiren ve 1001 Grup&apos;un
                kurumsal disiplinini yapı sektörüne taşıyan Yönetim Kurulu
                üyelerimiz.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {YONETIM_KURULU.map((member) => (
              <YonetimKuruluCard key={member.slug} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* 6. CTA — İş Birliği / Teklif */}
      <section
        data-testid="kurumsal-cta"
        className="bg-anthracite text-white py-20 lg:py-24 relative"
      >
        <div className="h-[1px] hairline-x absolute top-0 left-0 right-0" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-gold mb-4">İş Birliği</div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Projenizi birlikte planlayalım.
            </h2>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed font-light">
              Kurumsal iş birlikleri, iştirak yatırımları veya proje danışmanlığı için ekibimizle
              iletişime geçebilirsiniz.
            </p>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link
              to="/iletisim"
              data-testid="kurumsal-cta-teklif"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-hover text-white px-8 py-4 text-[12.5px] tracking-widest uppercase font-medium transition-colors"
            >
              İş Birliği Talebi Oluşturun
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Alt küçük link — kurumsal navigasyon */}
      <section className="bg-white py-10 border-t border-navy/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-[12px] tracking-[0.25em] uppercase text-anthracite/60">
            Sonraki Adım
          </div>
          <Link
            to="/faaliyet-alanlari"
            data-testid="kurumsal-next-faaliyet"
            className="group inline-flex items-center gap-3 text-navy hover:text-gold transition-colors text-[13px] tracking-widest uppercase font-medium"
          >
            Faaliyet Alanlarımızı İnceleyin
            <ArrowUpRight
              size={15}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>
      </section>
    </>
  );
}
